/**
 * ポータル: マイページ（PC・管理サイト側）
 * タレントカード風: 左=顔・所属・数字、右=基本情報・連絡先・緊急連絡先・資格・スキル・自己紹介。
 * 本人が編集できる（氏名・社員番号・配乗は users／配乗が正）。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { IdCard, Pencil, Phone, AlertTriangle, Award, Sparkles, UserRound } from 'lucide-react';
import * as api from '../../services/api';
import { PV, Avatar } from './PortalLayout';
import { fieldStyle, labelStyle } from './ui';

interface ProfileData {
  user: { id: string; user_name: string; employee_code: string; user_role: string; role_label: string; joined_at: string | null };
  assignment: { vessel_name: string; position: string | null } | null;
  profile: {
    kana: string | null; birth_date: string | null; blood_type: string | null; hire_date: string | null;
    phone: string | null; email: string | null; address: string | null;
    emergency_name: string | null; emergency_phone: string | null;
    licenses: string[]; skills: string[]; bio: string | null;
  };
  stats: { comp_balance: number; onboard_days_year: number; office_days_month: number };
}

const card: React.CSSProperties = { background: '#fff', border: `1px solid ${PV.border}`, borderRadius: 12, padding: '16px 18px', marginBottom: 14 };
const secTitle = (icon: React.ReactNode, label: string) => (
  <div style={{ fontSize: 13.5, fontWeight: 800, color: PV.accentDark, display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>{icon}{label}</div>
);

export default function PortalMyPage() {
  const [data, setData] = useState<ProfileData | null>(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    api.get<{ data: ProfileData }>('/portal/profile/me').then(r => setData(r.data)).catch(() => { /* noop */ });
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    if (!data) return;
    const p = data.profile;
    setForm({
      kana: p.kana ?? '', birth_date: p.birth_date ?? '', blood_type: p.blood_type ?? '', hire_date: p.hire_date ?? '',
      phone: p.phone ?? '', email: p.email ?? '', address: p.address ?? '',
      emergency_name: p.emergency_name ?? '', emergency_phone: p.emergency_phone ?? '',
      licenses: p.licenses.join('、'), skills: p.skills.join('、'), bio: p.bio ?? '',
    });
    setEdit(true); setMsg('');
  };

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await api.put<{ data: ProfileData }>('/portal/profile/me', {
        ...form,
        licenses: form['licenses']?.split(/[、,]/).map(s => s.trim()).filter(Boolean) ?? [],
        skills: form['skills']?.split(/[、,]/).map(s => s.trim()).filter(Boolean) ?? [],
      });
      setData(r.data); setEdit(false); setMsg('保存しました');
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : '保存に失敗しました'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="page-wrap"><div className="loading">読み込み中...</div></div>;
  const p = data.profile;

  const F = (key: string, placeholder = '', type = 'text') => (
    <input type={type} style={fieldStyle} placeholder={placeholder} value={form[key] ?? ''}
      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
  );
  const Row = ({ label, value, editNode }: { label: string; value: React.ReactNode; editNode?: React.ReactNode }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f3f5' }}>
      <span style={{ width: 110, flexShrink: 0, fontSize: 12.5, color: PV.textSub, fontWeight: 600 }}>{label}</span>
      {edit && editNode ? <span style={{ flex: 1 }}>{editNode}</span>
        : <span style={{ fontSize: 14, color: PV.text, fontWeight: 600 }}>{value || <span style={{ color: '#a8adb4', fontWeight: 500 }}>未登録</span>}</span>}
    </div>
  );
  const chips = (list: string[], accent: boolean) => list.length === 0
    ? <span style={{ fontSize: 13, color: '#a8adb4' }}>未登録</span>
    : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{list.map(x => (
        <span key={x} style={{ fontSize: 12.5, fontWeight: 700, padding: '5px 13px', borderRadius: 8, background: accent ? PV.accentSoft : '#eef2f7', color: accent ? PV.accentDark : '#3d4248' }}>{x}</span>
      ))}</div>;

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <UserRound size={20} /> マイページ
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {msg && <span style={{ fontSize: 12.5, color: PV.accentDark, fontWeight: 700 }}>{msg}</span>}
          {edit ? (
            <>
              <button className="btn" disabled={busy} onClick={() => setEdit(false)}>やめる</button>
              <button className="btn-p" disabled={busy} onClick={() => void save()}>{busy ? '保存中...' : '保存する'}</button>
            </>
          ) : (
            <button className="btn-p" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={startEdit}>
              <Pencil size={14} /> 編集する
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '320px minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
        {/* 左: タレントカード */}
        <div>
          <div style={{ ...card, textAlign: 'center', padding: '26px 18px' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}><Avatar name={data.user.user_name} size={84} /></div>
            <div style={{ fontSize: 12.5, color: PV.textSub, marginTop: 12 }}>{p.kana ?? ''}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: PV.text }}>{data.user.user_name}</div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap', marginTop: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 800, padding: '4px 12px', borderRadius: 20, background: PV.accentSoft, color: PV.accentDark }}>{data.user.role_label}</span>
              {data.assignment && (
                <span style={{ fontSize: 12, fontWeight: 800, padding: '4px 12px', borderRadius: 20, background: '#eef2f7', color: '#3d4248' }}>
                  {data.assignment.vessel_name}{data.assignment.position ? `・${data.assignment.position}` : ''}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: PV.textSub, marginTop: 8 }}>
              社員番号 {data.user.employee_code}{data.user.joined_at && `　／　入社 ${data.user.joined_at.replace(/-/g, '/')}`}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {[
              ['補償休日 残', `${Number.isInteger(data.stats.comp_balance) ? data.stats.comp_balance : data.stats.comp_balance.toFixed(1)}日`],
              ['今年の乗船', `${data.stats.onboard_days_year}日`],
              ['今月の出勤', `${data.stats.office_days_month}日`],
            ].map(([l, v]) => (
              <div key={l} style={{ background: '#fff', border: `1px solid ${PV.border}`, borderRadius: 10, padding: '12px 4px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: PV.textSub, fontWeight: 700 }}>{l}</div>
                <div style={{ fontSize: 17, fontWeight: 900, color: PV.accentDark, marginTop: 3 }}>{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 右: 情報 */}
        <div>
          <div style={card}>
            {secTitle(<IdCard size={15} />, '基本情報')}
            <Row label="フリガナ" value={p.kana} editNode={F('kana')} />
            <Row label="生年月日" value={p.birth_date?.replace(/-/g, '/')} editNode={F('birth_date', '', 'date')} />
            <Row label="血液型" value={p.blood_type} editNode={F('blood_type', '例: A型')} />
            <Row label="入社年月日" value={p.hire_date?.replace(/-/g, '/')} editNode={F('hire_date', '', 'date')} />
          </div>
          <div style={card}>
            {secTitle(<Phone size={15} />, '連絡先')}
            <Row label="電話" value={p.phone} editNode={F('phone', '', 'tel')} />
            <Row label="メール" value={p.email} editNode={F('email', '', 'email')} />
            <Row label="住所" value={p.address} editNode={F('address')} />
          </div>
          <div style={card}>
            {secTitle(<AlertTriangle size={15} />, '緊急連絡先')}
            <Row label="名前・続柄" value={p.emergency_name} editNode={F('emergency_name', '例: 田中 一郎（父）')} />
            <Row label="電話" value={p.emergency_phone} editNode={F('emergency_phone', '', 'tel')} />
          </div>
          <div style={card}>
            {secTitle(<Award size={15} />, '資格・免許')}
            {edit ? (<><label style={labelStyle}>「、」区切りで入力</label>{F('licenses', '例: 四級海技士（航海）、第一級海上特殊無線技士')}</>) : chips(p.licenses, true)}
          </div>
          <div style={card}>
            {secTitle(<Sparkles size={15} />, 'スキル・得意なこと')}
            {edit ? (<><label style={labelStyle}>「、」区切りで入力</label>{F('skills', '例: 機関整備、Excel、フォークリフト')}</>) : chips(p.skills, false)}
          </div>
          <div style={card}>
            {secTitle(<UserRound size={15} />, '自己紹介')}
            {edit ? (
              <textarea style={{ ...fieldStyle, minHeight: 110, resize: 'vertical' }} value={form['bio'] ?? ''}
                onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                placeholder="経歴・ひとこと・休暇の過ごし方など、自由にどうぞ" />
            ) : (
              <div style={{ fontSize: 14, color: p.bio ? PV.text : '#a8adb4', lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>{p.bio ?? '未登録'}</div>
            )}
          </div>
          <div style={{ fontSize: 12, color: PV.textSub }}>氏名・社員番号・配乗の変更は「ユーザー管理」から行います。</div>
        </div>
      </div>
    </div>
  );
}
